// src/utils/printProjectReport.js
//
// Shared "generate the full Cost Estimation Report and open it for
// print/Save as PDF" logic -- originally built inline inside
// EstimationSummary.jsx's handlePrintReport (and duplicated once already
// into DashboardExecutiveSummary.jsx for the portfolio Dashboard tab).
// Per explicit request, ProjectList.jsx's new "Estimated" column icon
// needs this same report, but for an ARBITRARY row's project rather than
// "whichever project is currently loaded on screen" -- which those two
// pages get from a chartRef/component state that only exists once that
// project's own page has actually rendered. Extracted here as a third,
// self-contained caller: given just a projectId, it fetches everything
// itself (including a throwaway offscreen chart just long enough to
// snapshot it), builds the identical HTML, and opens it exactly the same
// way. EstimationSummary.jsx/DashboardExecutiveSummary.jsx are left as
// they are (their own copies already work) -- this only backs the new
// ProjectList.jsx usage, so nothing about the existing Print Report
// buttons changes.
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

// Same categories/colors/gradient treatment as EstimationSummary.jsx's
// own "Proposed Vs Estimation" chart -- kept in sync by construction,
// all three read from the same /get_project_estimations_api response
// shape.
const CATEGORIES = [
  { key: "Construction", label: "Execution", percentKey: null },
  { key: "Design", label: "Design", percentKey: null },
  { key: "Supervision", label: "Supervision", percentKey: null },
  { key: "Other_Project", label: "Other Project", percentKey: null },
  { key: "Prelims_Amount", label: "Prelims", percentKey: "Prelims_Percent" },
  { key: "Risk_Amount", label: "Risk", percentKey: "Risk_Percent" },
  { key: "Inflation_Amount", label: "Inflation", percentKey: "Inflation_Percent" },
  { key: "Contingency_Amount", label: "Contingency", percentKey: "Contingency_Percent" },
  { key: "Location_Amount", label: "Location", percentKey: "Location_Percent" },
  { key: "Tender_Amount", label: "Tender", percentKey: "Tender_Percent" },
  { key: "Escalation_Amount", label: "Escalation", percentKey: "Escalation_Percent" },
];

const COLOR_BUDGET = "#2a78d6";
const COLOR_ESTIMATED = "#1baf7a";

const barGradient = (hex) => (ctx) => {
  const { chart } = ctx;
  const { chartArea } = chart;
  if (!chartArea) return hex;
  const g = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  g.addColorStop(0, hex);
  g.addColorStop(1, `${hex}b3`);
  return g;
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtM = (n) => `${fmt(Number(n || 0) / 1000000)} M`;
const fmtPct = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const fmtAxisMillions = (n) => `${(Number(n || 0) / 1000000).toLocaleString(undefined, { maximumFractionDigits: 0 })} M`;

const formatDurationYears = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  const str = String(raw).trim();
  if (!str) return "—";
  const parenYearsMatch = str.match(/\((\d+(\.\d+)?)\s*years?\)/i);
  if (parenYearsMatch) {
    const y = Number(parenYearsMatch[1]);
    return `${y} Year${y === 1 ? "" : "s"}`;
  }
  if (/year/i.test(str) && !/month/i.test(str)) return str;
  const match = str.match(/(\d+(\.\d+)?)/);
  if (!match) return str;
  const months = Number(match[1]);
  if (!months) return str;
  const years = months / 12;
  const roundedYears = Number.isInteger(years) ? years : Math.round(years * 10) / 10;
  return `${roundedYears} Year${roundedYears === 1 ? "" : "s"}`;
};

// Renders a throwaway Chart.js bar chart on an off-screen canvas (never
// attached to the visible layout -- 0x0 and appended to <body> just so
// Chart.js has a real, laid-out canvas to draw onto) purely to snapshot
// it as a PNG data: URL via toBase64Image(), then tears it down.
// Animation is disabled so the chart is fully drawn synchronously by the
// time `new Chart(...)` returns, instead of needing to wait a frame.
function snapshotChart(categories, budgetSeries, estimatedSeries, currencySymbol) {
  const canvas = document.createElement("canvas");
  // Bumped from 900x380 -- same 2.37:1 aspect ratio, higher resolution so
  // the chart stays crisp now that .chart-wrap img's print CSS renders it
  // much larger (see that rule's own comment for why it was too small).
  canvas.width = 1400;
  canvas.height = 591;
  canvas.style.position = "fixed";
  canvas.style.left = "-9999px";
  canvas.style.top = "0";
  document.body.appendChild(canvas);

  let chartImg = null;
  try {
    const chart = new ChartJS(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels: categories.map((c) => c.label),
        datasets: [
          { label: "Proposed Cost", data: budgetSeries, backgroundColor: barGradient(COLOR_BUDGET), maxBarThickness: 22 },
          { label: "Estimated Cost", data: estimatedSeries, backgroundColor: barGradient(COLOR_ESTIMATED), maxBarThickness: 22 },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        layout: { padding: { top: 8 } },
        plugins: {
          legend: {
            position: "top",
            align: "center",
            labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 16, font: { size: 12 } },
          },
          title: { display: false },
        },
        scales: {
          x: {
            title: { display: true, text: "Cost Breakdown", font: { size: 12, weight: "bold" }, color: "#52514e" },
            ticks: { maxRotation: 0, autoSkip: true, font: { size: 10 }, color: "#52514e" },
            grid: { display: false },
            border: { color: "#c3c2b7" },
            barPercentage: 0.85,
            categoryPercentage: 0.7,
          },
          y: {
            title: { display: true, text: `Cost (${currencySymbol})`, font: { size: 14, weight: "bold" }, color: "#52514e" },
            ticks: { callback: (val) => fmtAxisMillions(val), font: { size: 12 }, color: "#898781" },
            grid: { color: "#e1e0d9" },
            border: { display: false },
            grace: "10%",
          },
        },
        elements: { bar: { borderRadius: 4, borderSkipped: "start" } },
      },
    });
    chartImg = chart.toBase64Image();
    chart.destroy();
  } catch (err) {
    console.error("Failed to render offscreen chart for print report", err);
  } finally {
    canvas.remove();
  }
  return chartImg;
}

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// FIX, per explicit report on the OpCo logo fix above ("remove
// background and Cluster/2 same colour"): a plain CSS filter can't
// selectively recolor only the logo's ink while dropping its own canvas
// -- brightness(0) invert(1) treats every opaque pixel (background AND
// letters) identically, so on an opaque-background logo it either shows
// the logo in its own true colors (the white "chip" approach, a visible
// box the request just asked to remove) or flattens the whole thing to
// one indistinguishable white rectangle (the original "not showing
// logo" bug). This instead decodes the logo onto an offscreen canvas and
// repaints it pixel-by-pixel: bright/near-white pixels (the logo's own
// background, however opaque) become fully transparent, and every other
// pixel -- regardless of its original hue, so "Cluster"'s navy and the
// superscript "2"'s teal both included -- is repainted pure white, with
// a soft luminance ramp (not a hard cutoff) so anti-aliased edges stay
// smooth. The result is a true white silhouette of just the logo's
// marks with a transparent background, safe to place directly on the
// dark teal cover with no box behind it and no color inconsistency
// between its parts. Falls back to null (caller reverts to the plain
// OpCo-name text) if the image can't be decoded for any reason.
function toWhiteLogoSilhouette(srcDataUrl) {
  return new Promise((resolve) => {
    if (!srcDataUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          const a = px[i + 3];
          // Standard perceptual luminance. Pixels at/above ~235 (the
          // logo's own white/near-white canvas) fade fully to
          // transparent; pixels at/below ~200 (actual ink, any color)
          // stay fully opaque; the 35-unit band between the two is a
          // soft ramp so anti-aliased edges don't turn jagged.
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          const ink = Math.min(1, Math.max(0, (235 - luminance) / 35));
          px[i] = 255;
          px[i + 1] = 255;
          px[i + 2] = 255;
          px[i + 3] = a * ink;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("Failed to convert OpCo logo to a white silhouette", err);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = srcDataUrl;
  });
}

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Per request: users often paste multi-level bulleted text (Word-style
// "•"/"o"/"*" bullets) into these plain-textarea fields; a leading bullet
// character from the pasted text is stripped before escaping/rendering.
const stripBulletPrefix = (line) => line.replace(/^(?:[•*]|o)\s+/, "");
// REVERTED, per follow-up request ("dont start any no 1,2,3,4,5,6", then
// "start with . dot", then "remove . strart dot"): these multi-line
// fields get no auto-added marker at all now -- each line just renders
// as its own plain line, with only whatever the user actually typed.
const renderTextBlock = (text) => {
  const t = (text || "").toString().trim();
  if (!t) return "";
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1) {
    return `<div class="text-list">${lines.map((l) => `<div class="text-line">${esc(stripBulletPrefix(l))}</div>`).join("")}</div>`;
  }
  return `<p>${esc(stripBulletPrefix(t))}</p>`;
};
const section = (label, text) => {
  const html = renderTextBlock(text);
  return html ? `<div class="subsection"><h3>${esc(label)}</h3>${html}</div>` : "";
};

// "Project Introduction" is now edited with RichTextEditor.jsx (a
// contentEditable field), so its saved value is real HTML
// (bold/italic/underline/lists), not plain text -- running it through
// renderTextBlock()/esc() above would print the literal "<b>" tags
// instead of bold text. sanitizeRichHtml defensively strips anything
// unsafe before it's written into the print window's document (the
// editor itself only ever produces safe output, but this also renders
// whatever happens to be saved in the database).
const sanitizeRichHtml = (html) => {
  if (!html || typeof document === "undefined") return "";
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("script, style, iframe, object, embed, link, meta").forEach((el) => el.remove());
  // FIX, per follow-up ("underline text have bold but report not
  // showing"): when a run has BOTH bold and underline applied (e.g. a
  // "1. Engineering Assessment and Planning" heading), Chrome's
  // execCommand doesn't always wrap it in a <b>/<strong> tag -- it can
  // instead add font-weight directly as an inline style on the element
  // (alongside the <u> tag, which IS a real tag and was printing fine).
  // The old code stripped the whole "style" attribute except
  // text-align, so that inline font-weight/italic/underline styling was
  // silently thrown away and the text printed underlined-only, not
  // bold. Now a small safe whitelist of formatting properties is kept
  // (in addition to real <b>/<i>/<u> tags, which were never affected).
  const SAFE_STYLE_PROPS = ["textAlign", "fontWeight", "fontStyle", "textDecoration", "textDecorationLine"];
  container.querySelectorAll("*").forEach((el) => {
    const keep = {};
    if (el.style) {
      SAFE_STYLE_PROPS.forEach((prop) => {
        const v = el.style[prop];
        if (v) keep[prop] = v;
      });
    }
    [...el.attributes].forEach((attr) => {
      if (/^on/i.test(attr.name) || attr.name === "style" || attr.name === "href" || attr.name === "src") {
        el.removeAttribute(attr.name);
      }
    });
    Object.keys(keep).forEach((prop) => {
      el.style[prop] = keep[prop];
    });
  });
  return container.innerHTML;
};
const richTextSection = (label, html) => {
  const clean = sanitizeRichHtml(html);
  const isEmpty = !clean || clean.replace(/<[^>]+>/g, "").trim() === "";
  return isEmpty ? "" : `<div class="subsection"><h3>${esc(label)}</h3><div class="rich-text-content">${clean}</div></div>`;
};

/**
 * Fetches everything needed for one project's full Cost Estimation
 * Report (same content/sections as EstimationSummary.jsx's Print
 * Report) and opens it in a new tab for printing/Save as PDF.
 *
 * @param {Object} opts
 * @param {(url: string, options?: object) => Promise<Response>} opts.authFetch
 * @param {string} opts.API_BASE
 * @param {string} opts.projectId
 */
export async function printProjectReport({ authFetch, API_BASE, projectId }) {
  // FIX: window.open() used to be called at the very end of this
  // function, after every one of the awaits below (several real network
  // round trips). Opening a popup that late -- well after the click
  // handler's synchronous "user activation" window has closed -- is
  // exactly what several browsers' popup blockers key on: instead of
  // outright refusing it (which would have hit the `if (!win) throw`
  // guard), they let window.open() return a real window handle but treat
  // it as untrusted/backgrounded, so it shows up as a blank tab that
  // never actually receives the document.write() at the bottom of this
  // file and can leave the tab (and sometimes the opener) looking
  // unresponsive until it's closed. Opening it here, synchronously, as
  // the very first thing this function does -- before any await -- keeps
  // it inside the trusted user-gesture window every browser honors, so
  // the write/print at the end actually lands in it.
  const win = window.open("", "_blank");
  if (!win) {
    throw new Error("Please allow pop-ups for this site to print the report.");
  }
  // Immediate placeholder so the new tab never just looks blank/frozen
  // while the fetches below are in flight -- document.open() right
  // before the real write() further down clears this out first, so the
  // two writes don't get jumbled together into one document.
  win.document.write(
    `<!doctype html><title>Cost Estimation Report</title><body style="font-family:sans-serif;padding:60px;color:#666;">Generating report&hellip;</body>`
  );

  const summaryRes = await authFetch(`${API_BASE}/get_project_estimations_api?projectId=${encodeURIComponent(projectId)}`);
  const data = await summaryRes.json();
  if (!data?.success) {
    throw new Error(data?.error || "Failed to load this project's estimation summary.");
  }

  const [projectExtraJson, basisJson, elementScopeJson, subElementsJson] = await Promise.all([
    authFetch(`${API_BASE}/projects?search=${encodeURIComponent(projectId)}&pageSize=20`)
      .then((r) => r.json())
      .catch(() => null),
    authFetch(`${API_BASE}/basis_of_cost_api?Projectid=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .catch(() => null),
    authFetch(`${API_BASE}/element_scope?Projectid=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .catch(() => null),
    // Every saved Sub Element row for this project (project_sub_elements),
    // per explicit request: shown as its own table on the Basis of Cost
    // page, right before the Drawings table.
    authFetch(`${API_BASE}/get_project_sub_elements?project_id=${encodeURIComponent(projectId)}`)
      .then((r) => r.json())
      .catch(() => null),
  ]);

  let currencySymbol = "";
  try {
    const currRes = await authFetch(`${API_BASE}/currency`);
    const currJson = await currRes.json();
    currencySymbol = currJson?.data?.Currency_Symbol || "";
  } catch {
    // best-effort only
  }

  const rows = projectExtraJson?.data || [];
  const projectExtra = rows.find((r) => String(r.projectid) === String(projectId)) || rows[0] || null;

  let basisData = null;
  if (basisJson?.success && basisJson?.found) {
    const items = Array.isArray(basisJson.items) ? basisJson.items.map((it) => ({ ...it })) : [];
    if (Array.isArray(basisJson.files) && basisJson.files.length) {
      const remainingFiles = [...basisJson.files];
      items.forEach((row) => {
        const idx = remainingFiles.findIndex(
          (f) =>
            (f.drawingName || "").trim() === (row.drawingName || "").trim() &&
            (f.drawingNo || "").trim() === (row.drawingNo || "").trim()
        );
        if (idx !== -1) {
          const f = remainingFiles.splice(idx, 1)[0];
          row.attachedFile = { basisId: basisJson.basis_id, filename: f.filename };
        }
      });
    }
    basisData = { ...basisJson, items };
  }

  const elementScopeData = elementScopeJson?.success && elementScopeJson?.data ? elementScopeJson.data : null;
  const subElementRows = subElementsJson?.success && Array.isArray(subElementsJson.data) ? subElementsJson.data : [];

  const budgetSeries = CATEGORIES.map((c) => Number(data.budget?.[c.key] || 0));
  const estimatedSeries = CATEGORIES.map((c) => Number(data.estimated?.[c.key] || 0));
  const movementSeries = budgetSeries.map((b, i) => Number(estimatedSeries[i] - b));
  const budgetPercentSeries = CATEGORIES.map((c) => (c.percentKey ? Number(data.budget?.[c.percentKey] || 0) : null));
  const estimatedPercentSeries = CATEGORIES.map((c) => (c.percentKey ? Number(data.estimated?.[c.percentKey] || 0) : null));
  const budgetTotal = Number(data.budget?.Total_Sum || 0);
  const estimatedTotal = Number(data.estimated?.Total_Sum || 0);
  const movement = estimatedTotal - budgetTotal;

  const chartImg = snapshotChart(CATEGORIES, budgetSeries, estimatedSeries, currencySymbol);

  const logoUrl = `${window.location.origin}/assets/images/logo/CET-Full-icon.png`;
  const mataratLogoUrl = `${window.location.origin}/assets/images/logo/matarat_logo.png`;
  // See toWhiteLogoSilhouette's own comment above for why this replaces
  // the earlier white "chip" background -- per explicit request, the
  // OpCo logo now sits directly on the dark cover with no box behind it.
  const opcoLogoUrl = data.operatingCompanyLogo ? await toWhiteLogoSilhouette(data.operatingCompanyLogo) : null;
  const generatedOn = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const reportYear = new Date().getFullYear();

  // REVERTED, per follow-up request: Cost Summary table values are back
  // to plain currency figures (fmt) instead of Millions (fmtM) -- the
  // header cells still say "(currencySymbol)", not "(M)", so the values
  // now match their own column headers again.
  const tableRowsHtml = CATEGORIES.map(
    (c, i) => `
      <tr>
        <td class="label-cell">${c.label}</td>
        <td>${fmt(budgetSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(budgetPercentSeries[i])})</div>` : ""}</td>
        <td>${fmt(estimatedSeries[i])}${c.percentKey ? `<div class="pct">(${fmtPct(estimatedPercentSeries[i])})</div>` : ""}</td>
        <td>${fmt(movementSeries[i])}</td>
      </tr>
    `
  ).join("");
  // Per request: add a "Total Cost" row below Escalation in the printed
  // report's Cost Summary table only (the on-screen Estimation Summary /
  // Dashboard chart-and-table page is untouched) -- reuses the same
  // budgetTotal/estimatedTotal/movement figures already shown in the
  // tiles above this table.
  const totalRowHtml = `
      <tr class="total-row">
        <td class="label-cell"><b>Total Cost</b></td>
        <td><b>${fmt(budgetTotal)}</b></td>
        <td><b>${fmt(estimatedTotal)}</b></td>
        <td><b>${fmt(movement)}</b></td>
      </tr>
    `;

  // ---- Project Overview page ----
  const estDateDisplay = projectExtra?.estimation_date
    ? new Date(projectExtra.estimation_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "";

  // Per follow-up request, all 6 Project Overview fields are now edited
  // with RichTextEditor.jsx (not just Project Introduction), so all 6
  // are rendered with richTextSection (real HTML) instead of the plain-
  // text section() helper.
  const overviewSectionsHtml = [
    richTextSection("Project Introduction", projectExtra?.intro),
    richTextSection("Financial Overview", projectExtra?.financial),
    richTextSection("Inflation Forecast", projectExtra?.inflation),
    richTextSection("Contingency Summary", projectExtra?.contingency),
    richTextSection("Risk", projectExtra?.risks),
    richTextSection("Opportunity", projectExtra?.opportunities),
  ].join("");
  const hasOverviewContent = !!overviewSectionsHtml;
  const overviewPageHtml = hasOverviewContent
    ? `
      <div class="page page-with-header">
        <table class="page-repeat">
          <thead><tr><th>
            <div class="summary-header">
              <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
              <h2>Project Overview</h2>
            </div>
          </th></tr></thead>
          <tbody><tr><td>
            <div class="info-box">
              <div><b>Project Name</b> ${esc(data.projectName || "—")}</div>
              <div><b>Project ID</b> ${esc(projectId || "—")}</div>
              <div><b>Operating Company</b> ${esc(data.operatingCompany || "—")}</div>
              ${projectExtra?.class ? `<div><b>Estimation Class</b> ${esc(projectExtra.class)}</div>` : ""}
              ${projectExtra?.estimated_by ? `<div><b>Estimated By</b> ${esc(projectExtra.estimated_by)}</div>` : ""}
              ${estDateDisplay ? `<div><b>Estimation Date</b> ${esc(estDateDisplay)}</div>` : ""}
            </div>
            ${overviewSectionsHtml}
          </td></tr></tbody>
        </table>
      </div>
    `
    : "";

  // ---- Basis of Cost page ----
  // Sub Elements table -- every saved project_sub_elements row for this
  // project (Class, Asset Group, Asset Element, Main Element, Sub Element,
  // Description, Unit, Quantity, Rate, Cost), placed immediately before
  // the Drawings table per explicit request ("before drawing add
  // project_sub_elements all subelement information in print report").
  // REVERTED, per follow-up request: Rate and Cost are both back to their
  // normal (non-millions) currency-symbol formatting -- the earlier
  // Cost-in-Millions change (fmtCostM) is undone.
  const subElementRowsHtml = subElementRows
    .map(
      (it) => `
        <tr>
          <td class="label-cell">${esc(it.asset_class || "—")}</td>
          <td>${esc(it.asset_group || "—")}</td>
          <td>${esc(it.asset_element || "—")}</td>
          <td>${esc(it.main_element || "—")}</td>
          <td>${esc(it.sub_element || "—")}</td>
          <td>${esc(it.description || "—")}</td>
          <td>${esc(it.unit || "—")}</td>
          <td>${fmt(it.quantity)}</td>
          <td>${fmt(it.rate)}</td>
          <td>${fmt(it.cost)}</td>
        </tr>
      `
    )
    .join("");
  const subElementsTotalCost = subElementRows.reduce((sum, it) => sum + Number(it.cost || 0), 0);
  // REVERTED, per follow-up request: the printed report's "Estimated Cost"
  // tile again matches the on-screen Dashboard/Estimation Summary figure
  // (estimatedTotal = data.estimated.Total_Sum, the saved Flag='E'
  // Cost_Budget_Estimation row) instead of the Sub Elements total below.
  //
  // The "Proposed Cost" row added below uses budgetSeries[0] (the
  // Execution/Construction category from the Cost Summary table), NOT the
  // grand-total budgetTotal -- per explicit follow-up correction, since
  // project_sub_elements only ever represents Execution-category line
  // items, so its Proposed Cost figure should match the Cost Summary
  // table's "Execution" row, not the whole-project total.
  const subElementsSectionHtml = subElementRowsHtml
    ? `<div class="subsection"><h3>Estimated Cost</h3><table class="report-table sub-elements-table"><thead><tr><th>Class</th><th>Asset Group</th><th>Asset Element</th><th>Main Element</th><th>Sub Element</th><th>Description</th><th>Unit</th><th>Quantity</th><th>Rate (${currencySymbol})</th><th>Cost (${currencySymbol})</th></tr></thead><tbody>${subElementRowsHtml}<tr><td class="label-cell" colspan="9" style="text-align:right;">Estimated Cost (${currencySymbol})</td><td>${fmt(subElementsTotalCost)}</td></tr><tr><td class="label-cell" colspan="9" style="text-align:right;">Proposed Cost (${currencySymbol})</td><td>${fmt(budgetSeries[0])}</td></tr></tbody></table></div>`
    : "";

  const drawingRowsHtml = (basisData?.items || [])
    .filter((it) => (it.drawingName || it.DrawingName) || (it.drawingNo || it.DrawingNo) || it.url)
    .map(
      (it) => `
        <tr>
          <td class="label-cell">${esc(it.drawingName || it.DrawingName || "—")}</td>
          <td>${esc(it.drawingNo || it.DrawingNo || "—")}</td>
          <td>${esc(it.url || "—")}</td>
        </tr>
      `
    )
    .join("");

  const attachedDrawingRows = (basisData?.items || []).filter((it) => it.attachedFile);
  const drawingImagesHtml = (
    await Promise.all(
      attachedDrawingRows.map(async (it) => {
        try {
          const res = await authFetch(
            `${API_BASE}/basis_of_cost_api/files?basis_id=${encodeURIComponent(it.attachedFile.basisId)}&filename=${encodeURIComponent(it.attachedFile.filename)}`
          );
          if (!res.ok) return "";
          const blob = await res.blob();
          if (!blob) return "";
          const dataUrl = await blobToDataUrl(blob);
          const caption = it.drawingName || it.DrawingName || it.drawingNo || it.DrawingNo || "Drawing";
          // The "Upload (jpg/pdf)" field on Basis of Cost accepts PDFs too
          // (accept=".pdf,image/*") -- a PDF can't be embedded as an <img>,
          // but the file itself is still right here as a data: URL, so it
          // gets a clickable "View Drawing" link instead of being silently
          // dropped (which is what this used to do for anything that
          // wasn't an image, showing the table row with no indication a
          // file was even attached).
          if (blob.type && blob.type.startsWith("image/")) {
            return `<div class="drawing-image"><img src="${dataUrl}" alt="${esc(caption)}" /><div class="drawing-caption">${esc(caption)}</div></div>`;
          }
          return `<div class="drawing-image"><a class="drawing-file-link" href="${dataUrl}" target="_blank" rel="noopener noreferrer">${esc(caption)} &mdash; View Drawing (PDF)</a></div>`;
        } catch (err) {
          console.error("Failed to load drawing file for print report", err);
          return "";
        }
      })
    )
  ).join("");

  const basisSectionsHtml = [
    section("Assumption", basisData?.assumptions),
    section("Exclusion", basisData?.exclusions),
    subElementsSectionHtml,
    drawingRowsHtml
      ? `<div class="subsection"><h3>Drawings</h3><table class="report-table"><thead><tr><th>Drawing Name</th><th>Drawing No</th><th>URL</th></tr></thead><tbody>${drawingRowsHtml}</tbody></table>${drawingImagesHtml}</div>`
      : "",
  ].join("");
  const hasBasisContent = !!basisSectionsHtml;
  const basisPageHtml = hasBasisContent
    ? `
      <div class="page page-with-header">
        <table class="page-repeat">
          <thead><tr><th>
            <div class="summary-header">
              <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
              <h2>Basis of Cost</h2>
            </div>
          </th></tr></thead>
          <tbody><tr><td>
            ${basisSectionsHtml}
          </td></tr></tbody>
        </table>
      </div>
    `
    : "";

  // ---- Elemental Construction Scope page ----
  const CONSTRUCTION_SCOPE_FIELDS = [
    ["FacilitatingWorks", "Facilitating Works"],
    ["Substructure", "Substructure"],
    ["Superstructure", "Superstructure"],
    ["InternalFinishes", "Internal Finishes"],
    ["FittingsFurnishingsEquipment", "Fittings, Furnishings & Equipment"],
    ["Services", "Services"],
    ["CompletedBuildingsUnits", "Completed Buildings & Building Units"],
    ["WorksToExistingBuildings", "Works to Existing Buildings"],
    ["ExternalWorks", "External Works"],
  ];
  // Per follow-up request, these 9 fields are now edited with
  // RichTextEditor.jsx on ElementScopePage1.jsx too, so richTextSection
  // (real HTML) replaces the plain-text section() helper here as well.
  const constructionScopeSectionsHtml = CONSTRUCTION_SCOPE_FIELDS.map(([key, label]) =>
    richTextSection(label, elementScopeData?.[key])
  ).join("");
  const hasConstructionScopeContent = !!constructionScopeSectionsHtml;
  const constructionScopePageHtml = hasConstructionScopeContent
    ? `
      <div class="page page-with-header">
        <table class="page-repeat">
          <thead><tr><th>
            <div class="summary-header">
              <div class="brand"><img src="${logoUrl}" alt="Cost Estimation Tool" /><span>COST ESTIMATION TOOL</span></div>
              <h2>Elemental Construction Scope</h2>
            </div>
          </th></tr></thead>
          <tbody><tr><td>
            ${constructionScopeSectionsHtml}
          </td></tr></tbody>
        </table>
      </div>
    `
    : "";

  // ---- Cover page ----
  const coverHtml = `
    <div class="page cover">
      <div class="cover-decor">
        <div class="ribbon ribbon-diag"></div>
        <div class="ribbon ribbon-fold"></div>
      </div>
      <div class="cover-content">
        <div class="brand">
          <div class="brand-left">
            <img src="${logoUrl}" alt="Cost Estimation Tool" />
            <span>COST ESTIMATION TOOL</span>
            ${
              opcoLogoUrl
                ? `<span class="brand-sep">|</span><img class="brand-opco-logo" src="${opcoLogoUrl}" alt="${esc(data.operatingCompany || "OpCo")}" />`
                : data.operatingCompany
                ? `<span class="brand-sep">|</span><span class="brand-client">${esc(data.operatingCompany)}</span>`
                : ""
            }
          </div>
          <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
        </div>
        <hr class="divider" />
        <div class="meta">
          <div><b>Project</b> : ${esc(data.projectName || "—")}</div>
          ${projectExtra?.class ? `<div><b>Estimation</b> : ${esc(projectExtra.class)}</div>` : ""}
          <div><b>Date</b> : ${generatedOn}</div>
        </div>
      </div>
    </div>
  `;

  // ---- Back page ----
  const backPageHtml = `
    <div class="page back-page">
      <div class="cover-decor">
        <div class="ribbon ribbon-diag"></div>
        <div class="ribbon ribbon-fold"></div>
      </div>
      <div class="brand">
        <div class="brand-left">
          <img src="${logoUrl}" alt="Cost Estimation Tool" />
          <span>COST ESTIMATION TOOL</span>
        </div>
        <img class="matarat-logo" src="${mataratLogoUrl}" alt="MATARAT" />
      </div>
      <hr class="divider" />
      <div class="back-footer">
        <span>This report was generated by the Cost Estimation Tool.</span>
        <span>All Rights Reserved &copy; for MATARAT ${reportYear}.</span>
      </div>
    </div>
  `;

  // win was already opened (and given the "Generating report..."
  // placeholder) at the top of this function -- see the FIX comment
  // there for why. document.open() here discards that placeholder
  // document first, so this write() starts a clean document instead of
  // appending onto/alongside the placeholder's own <html>/<body>.
  win.document.open();
  win.document.write(`
    <html>
      <head>
        <title>Cost Estimation Report - ${esc(data.projectName || "Project")}</title>
        <style>
          /* FIX, per request ("add page no all pages"): a 12mm strip is
             reserved at the bottom of every physical printed page for a
             "Page X of Y" footer -- counter(page)/counter(pages) are
             filled in automatically by the browser's print engine for
             every page it lays out, so this repeats correctly no matter
             how many physical pages a section spills across (matching
             the repeating logo+title header fix above). This only shows
             up in the actual Print dialog/PDF output, never in the
             plain on-screen tab -- that's normal for @page rules. .page's
             own min-height is trimmed from 297mm to 285mm right below
             to match the smaller printable area this bottom margin
             leaves (otherwise every page's content would overflow by
             12mm and spill an extra near-empty page). */
          @page {
            size: A4;
            margin: 0 0 12mm 0;
            @bottom-center {
              content: "Page " counter(page) " of " counter(pages);
              font-family: Arial, Helvetica, sans-serif;
              font-size: 9px;
              color: #888;
            }
          }
          /* Without this, browsers drop every background color/image
             (the cover/back page's dark teal, the KPI tiles, table
             header rows, ...) once the actual Print dialog opens --
             they only show up in the on-screen preview -- unless the
             user manually ticks "Background graphics" in that dialog's
             "More settings". Forcing color-adjust here makes them
             print by default with no extra step needed. */
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
          /* FIX, per request: the floating "Print / Save as PDF" button
             that used to sit here is removed -- the report tab is a
             normal page, so the browser's own print/save controls
             (Ctrl+P / Cmd+P, or the browser's menu) already cover the
             same thing without an extra on-page button. There is still
             no auto-triggered win.print() call anywhere in this report
             -- see the earlier FIX note on win.focus() below for why
             that matters (a forced print dialog can make the main app
             tab look "stuck" while it's modal). .no-print/@media print
             below are kept in case any other floating/no-print element
             is added later. */
          /* FIX, per request ("direct open PDF format"): each .page
             below is a fixed A4-width sheet (210mm) with no centering,
             so on a normal browser tab -- much wider than 210mm -- it
             sat flush against the left edge with a huge blank white gap
             filling the rest of the window, looking broken/unfinished
             rather than like a report. Centering it on a neutral gray
             background (the same "page on a light tray" look Word/
             Google Docs' own print previews use) makes the on-screen
             tab look like a real document instead of a stray column of
             content; the @media print overrides right below strip all
             of that back out for the actual printed/saved-as-PDF pages,
             which only ever see the plain white .page itself, same as
             before this fix. */
          body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #212529; background: #e9ecef; }
          /* FIX, per request ("this page need fit in one page when
             printing"): the Executive Summary page's own content
             (header + info box + tiles + chart + an 11-row cost table)
             added up to just over one A4 sheet's printable height, so
             it was spilling a couple of rows onto a second printed
             page instead of staying self-contained. Trimmed here and
             in every rule below marked with the same FIX comment --
             slightly tighter page padding, spacing, and table row
             padding, plus a height cap on the chart image -- so the
             whole page reliably fits on one sheet with room to spare. */
          /* FIX, per follow-up (a genuinely blank "Page 3 of 9" showed
             up between the Executive Summary and Project Overview
             pages in the actual PDF): min-height was trimmed from
             297mm to 285mm above to match the new 12mm-shorter
             printable area (12mm is now reserved at the bottom of
             every page for the "Page X of Y" footer), matching it
             exactly. But the Executive Summary page was already tuned
             right up to the OLD limit ("fits with room to spare" was
             only ever a couple of mm, not a lot) -- and min-height
             forces .page to be at least that tall even where its real
             content is shorter, so the tiny bit of empty filler space
             at the very bottom of that forced box has to go somewhere
             once the printable area shrinks: it was spilling onto an
             otherwise-empty next page. Trimming another 2mm off as a
             safety margin (283mm, not 285mm) keeps that forced empty
             filler comfortably inside the real printable area so nothing
             spills over. */
          .page { width: 210mm; min-height: 283mm; padding: 14mm 16mm; position: relative; page-break-after: always; margin: 24px auto; box-shadow: 0 0 16px rgba(0,0,0,0.15); }
          .page:last-child { page-break-after: auto; }
          @media print {
            .no-print { display: none !important; }
            body { background: #fff; }
            .page { margin: 0; box-shadow: none; }
          }

          /* FIX, per follow-up (cover/back-page background showing a
             gap): the 12mm bottom margin added above for the "Page X
             of Y" footer applies to every physical printed page by
             default, including the cover and the closing back-page --
             but those two are a full-bleed dark background + diagonal
             ribbon design, and a browser's print margin is always left
             unpainted (it's reserved for header/footer text only), so
             that 12mm was cutting a plain white strip across the
             bottom of the design. "page: cover-page" assigns both to a
             named page, and the "@page cover-page" rule right below
             (CSS's mechanism for giving specific pages their own page
             setup) gives just these two their old zero-margin, no
             footer, full-bleed treatment back, while every other page
             still gets the 12mm footer margin from the plain @page
             rule above. min-height is restored to the full 297mm here
             too, since these two don't need the 12mm trimmed off for
             a footer they no longer reserve space for. */
          .cover, .back-page { page: cover-page; background: #0d3b3c; color: #fff; overflow: hidden; min-height: 297mm; }
          @page cover-page { size: A4; margin: 0; }
          .cover-content { position: relative; z-index: 2; }
          .cover .brand, .back-page .brand { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; position: relative; z-index: 2; }
          .cover .brand-left, .back-page .brand-left { display: flex; align-items: center; gap: 12px; }
          .cover .brand img, .back-page .brand img { height: 40px; width: auto; }
          .cover .brand span, .back-page .brand span { font-size: 20px; font-weight: 700; letter-spacing: 0.5px; }
          .cover .brand-sep { opacity: 0.5; font-weight: 400; }
          .cover .brand-client { font-size: 16px; }
          .cover .divider, .back-page .divider { border: none; border-top: 1px solid rgba(255,255,255,0.35); margin: 0 0 18px; position: relative; z-index: 2; }
          .cover .meta div { font-size: 18px; margin-bottom: 6px; position: relative; z-index: 2; }
          .cover .meta b { font-weight: 700; }
          /* Bolder "folded ribbon" background -- matches EstimationSummary.jsx
             / DashboardExecutiveSummary.jsx's cover/back-page decor: a
             diagonal band across the upper half plus a wide zigzag/chevron
             silhouette filling the lower half, both plain clip-path shapes
             (no image asset) over the same dark teal base. */
          .cover-decor { position: absolute; inset: 0; z-index: 1; overflow: hidden; }
          .cover-decor .ribbon { position: absolute; inset: 0; }
          .cover-decor .ribbon-diag { background: rgba(255,255,255,0.09); clip-path: polygon(100% 19%, 100% 31%, 0% 66%, 0% 54%); }
          .cover-decor .ribbon-fold { background: rgba(255,255,255,0.07); clip-path: polygon(0% 100%, 0% 61%, 46% 50%, 63% 74%, 88% 50%, 100% 61%, 100% 100%); }
          .cover .matarat-logo, .back-page .matarat-logo { height: 40px; width: auto; filter: brightness(0) invert(1); }
          /* OpCo logo (from the OpCo table's Logo column, opco_logo_column.sql)
             replaces the plain OpCo-name text next to "COST ESTIMATION" on
             the cover. Was force-recolored via a CSS filter (brightness(0)
             invert(1)), which flattened an opaque-background logo like
             cluster2's into one indistinguishable white rectangle -- then
             a white "chip" background was tried instead, which fixed
             legibility but left a visible box the logo's own creators
             hadn't put there. FIX, per explicit follow-up request ("remove
             background and Cluster/2 same colour"): toWhiteLogoSilhouette
             now pre-processes the logo on a canvas before it ever reaches
             this <img> -- its own background is already transparent and
             every mark (any original color) already solid white -- so no
             chip and no filter are needed here at all; the logo just sits
             directly on the dark cover like the MATARAT mark beside it. */
          /* Per request: shrink the OpCo logo (e.g. "Cluster2") on the
             print report cover page -- was height:32px/max-width:140px. */
          .cover .brand-opco-logo { height: 22px; width: auto; max-width: 100px; object-fit: contain; }

          .back-page { display: flex; flex-direction: column; justify-content: flex-end; }
          /* FIX, per request: the two closing lines used to stack on
             separate lines -- now on one row, the credit text pushed to
             the right via space-between, matching the request that
             "All Rights Reserved..." sit on the right side. */
          .back-page .back-footer { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 12px; opacity: 0.85; position: relative; z-index: 2; }

          /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
          .summary-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #124d52; padding-bottom: 8px; margin-bottom: 14px; }
          .summary-header .brand { display: flex; align-items: center; gap: 10px; }
          .summary-header .brand img { height: 28px; width: auto; }
          .summary-header .brand span { font-weight: 700; font-size: 13px; color: #124d52; }
          .summary-header h2 { font-size: 16px; font-weight: 700; color: #124d52; letter-spacing: 0.5px; margin: 0; text-transform: uppercase; }

          /* FIX, per request ("print show the area name with logo print
             all pages"): each content page's .summary-header used to sit
             directly inside the .page div, so when a section's content
             (a long Project Introduction, lots of drawings, etc.) is
             taller than one physical sheet, the browser just spills the
             overflow onto extra printed pages with no header at all --
             the header only ever showed once, at the very top of that
             section. Wrapping the whole page's content in a <table> with
             the header inside <thead> instead fixes this: browsers
             natively repeat a table's <thead> on every physical page a
             table spans when printing, so the logo + section name now
             appears at the top of every printed sheet, not just the
             section's first one. table.page-repeat resets the table/
             cell styling back to plain block layout (no borders/
             striping) so this is invisible other than the repeating
             header. */
          table.page-repeat { width: 100%; border-collapse: collapse; }
          table.page-repeat > thead > tr > th, table.page-repeat > tbody > tr > td { padding: 0; text-align: left; font-weight: normal; }

          /* FIX, per follow-up ("give some gap in uper site" -- a
             continuation page's header was sitting flush against the
             paper edge with no breathing room, unlike a section's first
             page): a box's own top padding (.page's 14mm) is only
             re-applied on the FIRST physical page a box lands on --
             browsers don't repeat top/bottom padding on the
             continuation pages of a box that spills across sheets, so
             .page's 14mm top gap was missing on every page after the
             first one for a page. The fix moves that top gap onto the
             *table's* <thead> instead of the outer .page div --
             page-with-header turns off .page's own top padding, and
             the same 14mm now lives on the repeating header cell
             instead, so it applies identically on every physical page
             (first and every continuation one), matching the first
             page's look everywhere. */
          .page.page-with-header { padding-top: 0; }
          table.page-repeat > thead > tr > th { padding-top: 14mm; }

          /* FIX (fit-one-page): tighter padding/margin -- see .page above. */
          .info-box { background: #f4f5f7; border-radius: 4px; padding: 10px 18px; margin-bottom: 14px; font-size: 13px; }
          .info-box div { margin-bottom: 4px; }
          .info-box b { display: inline-block; width: 160px; }

          /* FIX (fit-one-page): tighter margin/padding -- see .page above. */
          .tiles { display: flex; gap: 10px; margin-bottom: 14px; }
          .tiles .tile { flex: 1; border-radius: 4px; padding: 9px 8px; text-align: center; color: #fff; }
          .tiles .tile .val { font-size: 15px; font-weight: 700; }
          .tiles .tile .lbl { font-size: 10px; font-weight: 600; margin-top: 4px; }

          /* FIX (fit-one-page), then re-FIXED per explicit request ("chart
             showing very small size"): the original 62mm max-height cap
             sat BELOW the chart's own natural width-constrained height
             (~75mm at this page's ~178mm content width and the chart's
             2.37:1 aspect ratio) -- so it was the binding constraint,
             shrinking the chart to noticeably less than full page width
             on top of being short. Raised to 100mm, comfortably above
             that ~75mm natural height, so max-width:100% (not this cap)
             is what actually sizes the chart now -- it renders at the
             page's full content width with proportional height, both
             visibly bigger than before. The rest of this page's content
             (info-box/tiles/11-row table) was re-measured to confirm this
             still leaves headroom to fit one printed A4 sheet. */
          .chart-wrap { margin-bottom: 12px; text-align: center; }
          /* FIX (extra safety margin for the blank-page-3 fix above):
             trimmed another 6mm off the chart's own height cap so the
             Executive Summary page has a bit more headroom to reliably
             stay on one sheet even with the smaller printable area. */
          .chart-wrap img { max-width: 100%; max-height: 94mm; width: auto; height: auto; }

          table.report-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
          /* FIX (fit-one-page): shaved 3px off top+bottom row padding --
             multiplied across the table's 11 cost-category rows plus its
             header row, this is the single biggest space saving here. */
          table.report-table th, table.report-table td { border: 1px solid #dcdcdc; padding: 5px 6px; text-align: center; }
          table.report-table th { background: #124d52; color: #fff; font-size: 10.5px; text-transform: uppercase; }
          table.report-table th:first-child, table.report-table td.label-cell { text-align: left; }
          table.report-table td.label-cell { font-weight: 600; }
          /* FIX (fit-one-page): trimmed margin -- see .page above. */
          table.report-table .pct { font-size: 9px; color: #777; margin-top: 1px; }
          /* Per request: "Total Cost" row appended below Escalation in
             the Cost Summary table (print report only) -- a light tint
             plus a heavier top border sets it apart as the summary line. */
          table.report-table tr.total-row td { background: #eef3f2; border-top: 2px solid #124d52; }

          /* Sub Elements table has 10 columns (Class, Asset Group, Asset
             Element, Main Element, Sub Element, Description, Unit,
             Quantity, Rate, Cost) -- tighter font/padding than the
             standard .report-table so it still fits the 210mm page width
             legibly, with description text wrapping instead of
             overflowing its cell. */
          table.report-table.sub-elements-table { table-layout: fixed; }
          table.report-table.sub-elements-table th,
          table.report-table.sub-elements-table td { padding: 4px 4px; word-break: break-word; }

          .subsection { margin-bottom: 16px; }
          .subsection h3 { font-size: 12.5px; font-weight: 700; color: #124d52; margin: 0 0 6px; text-transform: uppercase; letter-spacing: 0.3px; }
          .subsection p { font-size: 12px; line-height: 1.6; margin: 0; color: #333; }
          .subsection div.text-list { margin: 0; font-size: 12px; line-height: 1.6; color: #333; }
          .subsection div.text-list .text-line { margin-bottom: 4px; }
          .subsection .rich-text-content { font-size: 12px; line-height: 1.6; color: #333; white-space: pre-wrap; }
          .subsection .rich-text-content p, .subsection .rich-text-content div { margin: 0 0 6px; }
          .subsection .rich-text-content ul, .subsection .rich-text-content ol { margin: 0 0 6px; padding-left: 20px; }
          .subsection .rich-text-content li { margin: 0 0 2px; }
          /* FIX, per follow-up ("report not showing all rich text"):
             forcing every <b>/<strong> run to display:block (added for
             the numbered-heading request) was too broad -- it also
             broke any bold WORD or PHRASE in the middle of a normal
             sentence onto its own line, fragmenting ordinary paragraphs
             and making mixed plain/bold text look like it was missing
             content. Bold is back to normal inline behavior; headings
             still land on their own line because each one is typed as
             its own paragraph/div in the editor (block-level by default
             already) and white-space: pre-wrap above preserves that
             structure exactly as typed, with no need to force every
             bold run onto its own line. */
          .subsection .rich-text-content b, .subsection .rich-text-content strong { font-weight: 700; }

          .drawing-image { margin-top: 10px; text-align: center; }
          .drawing-image img { max-width: 100%; max-height: 450px; border: 1px solid #dcdcdc; padding: 4px; background: #fff; }
          .drawing-image .drawing-caption { font-size: 10.5px; color: #666; margin-top: 4px; }
          .drawing-file-link { display: inline-block; padding: 8px 16px; border: 1px solid #02bc9c; border-radius: 4px; color: #02bc9c; text-decoration: none; font-size: 12px; font-weight: 600; }
        </style>
      </head>
      <body>
        ${coverHtml}

        <div class="page page-with-header">
          <table class="page-repeat">
            <thead><tr><th>
              <div class="summary-header">
                <div class="brand">
                  <img src="${logoUrl}" alt="Cost Estimation Tool" />
                  <span>COST ESTIMATION TOOL</span>
                </div>
                <h2>Executive Summary</h2>
              </div>
            </th></tr></thead>
            <tbody><tr><td>
              <div class="info-box">
                <div><b>Project Name</b> ${esc(data.projectName || "—")}</div>
                <div><b>Operating Company</b> ${esc(data.operatingCompany || "—")}</div>
                <div><b>Project Duration</b> ${esc(formatDurationYears(data.duration))}</div>
              </div>

              <div class="tiles">
                <div class="tile" style="background:#3d5166"><div class="val">${esc(data.operatingCompany || "—")}</div><div class="lbl">COMPANY NAME</div></div>
                <div class="tile" style="background:#7d5ba6"><div class="val">${esc(formatDurationYears(data.duration))}</div><div class="lbl">PROJECT DURATION</div></div>
                <div class="tile" style="background:#147196"><div class="val">${currencySymbol} ${fmtM(budgetTotal)}</div><div class="lbl">PROPOSED COST</div></div>
                <div class="tile" style="background:#8cc653"><div class="val">${currencySymbol} ${fmtM(estimatedTotal)}</div><div class="lbl">ESTIMATED COST</div></div>
                <div class="tile" style="background:#c81017"><div class="val">${currencySymbol} ${fmtM(movement)}</div><div class="lbl">VARIANCE</div></div>
              </div>

              <div class="chart-wrap">
                ${chartImg ? `<img src="${chartImg}" alt="Proposed Vs Estimation chart" />` : ""}
              </div>

              <table class="report-table">
                <thead>
                  <tr>
                    <th>Cost Summary</th>
                    <th>Proposed Cost (${currencySymbol})</th>
                    <th>Estimated Cost (${currencySymbol})</th>
                    <th>Variance (${currencySymbol})</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRowsHtml}
                  ${totalRowHtml}
                </tbody>
              </table>
            </td></tr></tbody>
          </table>
        </div>

        ${overviewPageHtml}
        ${basisPageHtml}
        ${constructionScopePageHtml}
        ${backPageHtml}
      </body>
    </html>
  `);
  win.document.close();
  // FIX: win.focus() used to run here, forcing the report tab to the
  // foreground the instant it opened -- per explicit request, the user
  // needs to keep navigating the Cost Estimation Tool itself (sidebar
  // links, other pages) while the report is open, and a stolen-focus
  // tab switch is exactly what got in the way of that: this tab stayed
  // the active/frontmost one until the user manually switched away.
  // Leaving this out means the report opens quietly in a background
  // tab -- this tab never loses focus, so every link and button here
  // keeps working immediately -- and the user can switch to the report
  // tab whenever they actually want to look at or print it.
  //
  // Also no more auto-triggered win.print() here, and no floating
  // "Print / Save as PDF" button either -- the report tab just opens and
  // sits there in the background; printing/saving happens whenever the
  // user chooses, via the browser's own print/save controls (Ctrl+P /
  // Cmd+P, or its menu).
}
